import React, { useState } from 'react';
import Image from 'components/AppImage';
import Button from 'components/ui/Button';
import Icon from 'components/AppIcon';

const AccommodationShowcase = ({ onBookNowClick, onViewDetailsClick }) => {
  const [hoveredRoom, setHoveredRoom] = useState(null);

  const accommodations = [
    {
      id: 1,
      name: 'Deluxe Rooms',
      subtitle: 'Modern Comfort',
      image: 'https://images.pexels.com/photos/271624/pexels-photo-271624.jpeg',
      gallery: [
        'https://images.pexels.com/photos/271624/pexels-photo-271624.jpeg',
        'https://images.pexels.com/photos/164595/pexels-photo-164595.jpeg'
      ],
      priceFrom: 3500,
      capacity: '2-3 Guests',
      size: '350 sq ft',
      features: [
        'Air conditioning',
        'Private balcony',
        'Mini refrigerator',
        'Free Wi-Fi'
      ],
      amenities: ['TV', 'Safe', 'Kettle', 'Bathroom'],
      rating: 4.6,
      reviews: 124
    },
    {
      id: 2,
      name: 'River View Cottages',
      subtitle: 'Scenic Waterfront',
      image: 'https://images.pixabay.com/photo/2016/11/18/17/20/living-room-1835923_1280.jpg',
      gallery: [
        'https://images.pixabay.com/photo/2016/11/18/17/20/living-room-1835923_1280.jpg',
        'https://images.pixabay.com/photo/2017/03/28/12/11/chairs-2181947_1280.jpg'
      ],
      priceFrom: 4500,
      capacity: '2-4 Guests',
      size: '450 sq ft',
      features: [
        'River view',
        'Private deck',
        'Kitchenette',
        'Nature sounds'
      ],
      amenities: ['Fireplace', 'Hammock', 'BBQ', 'Kayak'],
      rating: 4.8,
      reviews: 89
    },
    {
      id: 3,
      name: 'Luxury Tents',
      subtitle: 'Glamping Experience',
      image: 'https://images.unsplash.com/photo-1504280390367-361c6d9f38f4',
      gallery: [
        'https://images.unsplash.com/photo-1504280390367-361c6d9f38f4',
        'https://images.unsplash.com/photo-1520637836862-4d197d17c90a'
      ],
      priceFrom: 5500,
      capacity: '2 Guests',
      size: '300 sq ft',
      features: [
        'Canvas luxury',
        'Wooden flooring',
        'Eco-friendly',
        'Star gazing deck'
      ],
      amenities: ['Telescope', 'Campfire', 'Lanterns', 'Organic'],
      rating: 4.9,
      reviews: 67
    },
    {
      id: 4,
      name: 'Tree Houses',
      subtitle: 'Elevated Adventure',
      image: 'https://images.pexels.com/photos/1029599/pexels-photo-1029599.jpeg',
      gallery: [
        'https://images.pexels.com/photos/1029599/pexels-photo-1029599.jpeg',
        'https://images.pexels.com/photos/1134166/pexels-photo-1134166.jpeg'
      ],
      priceFrom: 6500,
      capacity: '2-3 Guests',
      size: '400 sq ft',
      features: [
        'Treetop views',
        'Rope bridge access',
        'Bird watching',
        'Unique architecture'
      ],
      amenities: ['Binoculars', 'Swing', 'Ladder', 'Platform'],
      rating: 4.7,
      reviews: 45
    }
  ];

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0
    })?.format(amount);
  };

  return (
    <section className="py-16 lg:py-24 bg-background">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center space-x-2 bg-primary/10 rounded-full px-4 py-2 mb-4">
            <Icon name="Bed" size={16} className="text-primary" />
            <span className="text-primary font-medium text-sm">Accommodations</span>
          </div>
          <h2 className="text-3xl lg:text-4xl font-heading font-bold text-foreground mb-4">
            Stunning Places to Stay
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            From modern deluxe rooms to unique tree houses, find the perfect accommodation that matches your adventure style
          </p>
        </div>

        {/* Accommodations Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {accommodations?.map((room) => (
            <div
              key={room?.id}
              className="bg-card rounded-2xl shadow-organic-lg overflow-hidden hover-lift group cursor-pointer"
              onMouseEnter={() => setHoveredRoom(room?.id)}
              onMouseLeave={() => setHoveredRoom(null)}
              onClick={() => onViewDetailsClick(room)}
            >
              {/* Image Section */}
              <div className="relative h-48 overflow-hidden">
                <Image
                  src={hoveredRoom === room?.id && room?.gallery?.[1] ? room?.gallery?.[1] : room?.image}
                  alt={room?.name}
                  className="w-full h-full object-cover group-hover:scale-105 transition-organic"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent"></div>
                
                {/* Rating Badge */}
                <div className="absolute top-4 left-4 bg-card/90 backdrop-blur-sm rounded-full px-3 py-1 flex items-center space-x-1">
                  <Icon name="Star" size={14} className="text-accent fill-current" />
                  <span className="text-sm font-medium text-card-foreground">{room?.rating}</span>
                  <span className="text-xs text-muted-foreground">({room?.reviews})</span>
                </div>

                {/* Quick Book Button */}
                <div className="absolute top-4 right-4 opacity-0 group-hover:opacity-100 transition-organic">
                  <Button
                    variant="default"
                    size="sm"
                    iconName="Calendar"
                    iconPosition="left"
                    onClick={(e) => {
                      e?.stopPropagation();
                      onBookNowClick(room);
                    }}
                    className="bg-primary/90 hover:bg-primary text-primary-foreground"
                  >
                    Book
                  </Button>
                </div>

                {/* Price Badge */}
                <div className="absolute bottom-4 left-4 bg-card/90 backdrop-blur-sm rounded-lg px-3 py-2">
                  <div className="text-lg font-heading font-bold text-primary">
                    {formatCurrency(room?.priceFrom)}
                  </div>
                  <div className="text-xs text-muted-foreground">per night</div>
                </div>
              </div>

              {/* Content Section */}
              <div className="p-4">
                <div className="mb-3">
                  <h3 className="text-lg font-heading font-bold text-card-foreground mb-1">
                    {room?.name}
                  </h3>
                  <p className="text-sm text-muted-foreground mb-2">
                    {room?.subtitle}
                  </p>
                  <div className="flex items-center space-x-4 text-xs text-muted-foreground">
                    <div className="flex items-center space-x-1">
                      <Icon name="Users" size={12} />
                      <span>{room?.capacity}</span>
                    </div>
                    <div className="flex items-center space-x-1">
                      <Icon name="Maximize" size={12} />
                      <span>{room?.size}</span>
                    </div>
                  </div>
                </div>

                {/* Features */}
                <div className="mb-4">
                  <div className="grid grid-cols-2 gap-1">
                    {room?.features?.slice(0, 4)?.map((feature, index) => (
                      <div key={index} className="flex items-center space-x-1 text-xs text-muted-foreground">
                        <Icon name="Check" size={10} className="text-success flex-shrink-0" />
                        <span className="truncate">{feature}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Amenities Icons */}
                <div className="flex items-center justify-between">
                  <div className="flex space-x-2">
                    {room?.amenities?.slice(0, 3)?.map((amenity, index) => (
                      <div key={index} className="w-6 h-6 bg-muted rounded-full flex items-center justify-center">
                        <span className="text-xs text-muted-foreground font-medium">
                          {amenity?.charAt(0)}
                        </span>
                      </div>
                    ))}
                    {room?.amenities?.length > 3 && (
                      <div className="w-6 h-6 bg-primary/10 rounded-full flex items-center justify-center">
                        <span className="text-xs text-primary font-medium">
                          +{room?.amenities?.length - 3}
                        </span>
                      </div>
                    )}
                  </div>
                  
                  <Button
                    variant="ghost"
                    size="sm"
                    iconName="ArrowRight"
                    iconPosition="right"
                    onClick={(e) => {
                      e?.stopPropagation();
                      onViewDetailsClick(room);
                    }}
                    className="text-primary hover:text-primary/80 p-1"
                  >
                    Details
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Bottom CTA */}
        <div className="text-center mt-12">
          <p className="text-muted-foreground mb-4">
            Can't decide? Take a virtual tour of all our accommodations
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              variant="outline"
              size="lg"
              iconName="Camera"
              iconPosition="left"
              onClick={() => window.location.href = '/photo-gallery'}
            >
              View Photo Gallery
            </Button>
            <Button
              variant="default"
              size="lg"
              iconName="Phone"
              iconPosition="left"
            >
              Speak to Our Expert
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AccommodationShowcase;